import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MediaApiService } from '@simOn/common/media';
import { NotificationService } from '@simOn/common/notification';
import { CreateImageUrl, resolveAvailableSize } from '@simOn/common/upload-image';
import { BreakpointService } from '@simOn/ui/breakpoint';
import { SimButtonModule } from '@simOn/ui/sim-button';
import { SimIconComponent } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { SystemMessageComponent } from '@simOn/ui/system-message';
import { ErrorMessages } from '@simOn/utils';
import { derivedAsync } from 'ngxtension/derived-async';
import { catchError, fromEvent, of, switchMap } from 'rxjs';
import { AssetsAttachmentTypes, AttachmentData, ImageBaseDirective } from '../../sim-carousel';

@Component({
  selector: 'common-slideshow',
  templateUrl: 'sim-slideshow.component.html',
  styleUrls: ['sim-slideshow.component.scss'],
  imports: [SimButtonModule, SimSpinnerComponent, SimIconComponent, ImageBaseDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SimSlideshowComponent {
  private readonly _breakpointService = inject(BreakpointService);
  private readonly _mediaApiService = inject(MediaApiService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly image = derivedAsync(() => {
    const currentIndex = this.currentIndex();
    const collection = this.collection();
    const isLocalFile = this.isLocalFile();

    return this._breakpointService.breakpointChange$.pipe(
      switchMap(() => {
        const { url, availableSizes, urlWithToken } = collection[currentIndex];
        if (urlWithToken || isLocalFile) return of(url);
        const size = resolveAvailableSize(availableSizes, 'Big');
        return this._generateImageUrl(size ? { url, availableSizes, size } : { url });
      }),
      catchError(() => {
        this.imageLoaded.set(true);
        this._showDefaultError();
        return of(undefined);
      }),
      takeUntilDestroyed(this._destroyRef)
    );
  });

  protected readonly hasNext = computed(() => {
    const collectionLength = this.collectionLength();
    const currentIndex = this.currentIndex();
    return collectionLength > 0 && collectionLength > currentIndex;
  });

  protected readonly hasPrevious = computed(() => {
    const currentIndex = this.currentIndex();
    return currentIndex > 0;
  });

  protected readonly collectionLength = computed(() => {
    const collection = this.collection();
    return (collection?.length ?? 0) - 1;
  });

  protected readonly localFileUrl = computed(() => {
    const collection = this.collection();
    const currentIndex = this.currentIndex();
    return collection[currentIndex].url;
  });

  protected readonly isLocalFile = computed(() => {
    const localFileUrl = this.localFileUrl();
    return localFileUrl.includes('base64');
  });

  protected readonly currentIndexEffect = effect(() => {
    const currentIndex = this.currentIndex();
    this.emitCurrentIndex.emit(currentIndex);
  });

  protected readonly navigationElementEvent = toSignal(
    fromEvent<KeyboardEvent>(document, 'keyup').pipe(takeUntilDestroyed(this._destroyRef))
  );

  protected readonly navigationElementEventEffect = effect(() => {
    const event = this.navigationElementEvent();
    if (!event) return;

    if (event.key === 'ArrowRight') {
      return this.nextElement();
    }

    if (event.key === 'ArrowLeft') {
      return this.previousElement();
    }
  });

  protected readonly imageLoaded = signal(false);

  readonly collection = input.required<AttachmentData[]>();
  readonly type = input.required<AssetsAttachmentTypes>();
  readonly currentIndex = model(0);
  readonly emitCurrentIndex = output<number>();

  previousElement(): void {
    if (!this.hasPrevious()) return;

    this.currentIndex.update((value) => value - 1);
    this.imageLoaded.set(false);
  }

  nextElement(): void {
    if (!this.hasNext()) return;

    this.currentIndex.update((value) => value + 1);
    this.imageLoaded.set(false);
  }

  private readonly _generateImageUrl = (value: CreateImageUrl) => {
    return this._mediaApiService.generateBlobUrlForImageDownload(value);
  };

  private _showDefaultError() {
    const instance = this._notificationService.openFromComponent(SystemMessageComponent, {
      verticalPosition: 'bottom',
      horizontalPosition: 'center'
    }).instance;
    instance.message.set(ErrorMessages.default);
    instance.type.set('error');
  }
}
